import { createAsyncThunk } from "@reduxjs/toolkit";
import { axios_api, server_base_endpoints } from "../../api";
import { getUniqueId } from "../../helpers";
import { IAutomationState, IConditionTableRow, ICreateWorkflow, IGraphNode, IWorkFlowAction, IWorkFlowCondition, IWorkflowFinishEmail, IWorkflowFinishEmailAPIPayloadResponse, IWorkflowGET } from "./automation.types";
import { parseFinialEmailApiToLocalPayload } from "./automation.helpers";
import { createAsyncThunkPostWrapper } from "../store.wrapper";
import { IStoreState } from "../store.types";
import { getSearchQuery, IQueryParams } from "../../common";
import { closeLoaderWithMessage, displaySnackBarDialog, openLoaderWithMessage } from "../common";

const workflow = server_base_endpoints.workflow

export const fetchWorkflowListAsync = createAsyncThunk<{ data: IWorkflowGET[], count: number }, IQueryParams>(
    "automation/fetchWorkflowListAsync", async (queryParams) => {
        const searchQuery = getSearchQuery(queryParams);
        const response = await axios_api.get(`${workflow.get_workflow_basic}${searchQuery}`);
        const data: IWorkflowGET[] = response.data.data;
        const count = response.data.totalRecords;
        return { data, count };
    });

export const fetchAutomationAsync = createAsyncThunk<IAutomationState["graphData"], string>(
    "automation/fetchAutomationAsync", async (code) => {
        const response = await axios_api.get(`${workflow.get_workflow_all_definition}?workflow_basic_code=${code}`);
        const data = response.data.data;
        let finalGraphData: IAutomationState["graphData"] = {
            nodes: [],
            edges: [],
            workflow_basic_code: null,
        };
        if (data.length > 0) {
            for (const item of data) {
                const { nodes, ...rest } = item;
                if (
                    nodes &&
                    (rest.status === "ACTIVE" ||
                        item.nodes.type === "parentWorkflowNode")
                ) {
                    if (item.nodes.type === "performEmailAction") {
                        const { nodes, ...rest } =
                            parseFinialEmailApiToLocalPayload(item);
                        const graphNode: IGraphNode = {
                            id: item.nodes.id,
                            data: {
                                label: item.nodes.data.label,
                                payload: rest as IWorkflowFinishEmail,
                            },
                            position: item.nodes.position,
                            type: item.nodes.type,
                        };

                        finalGraphData.nodes.push(graphNode);
                    } else {
                        const graphNode: IGraphNode = {
                            id: item.nodes.id,
                            data: {
                                label: item.nodes.data.label,
                                payload: rest as IWorkFlowCondition,
                            },
                            position: item.nodes.position,
                            type: item.nodes.type,
                        };

                        finalGraphData.nodes.push(graphNode);
                    }
                }
            }

            const placeholderId = getUniqueId();

            finalGraphData.nodes.push({
                id: placeholderId,
                data: { label: "+", payload: {} as any },
                position: { x: 0, y: 0 },
                type: "placeholder",
            });

            for (let i = 0; i < finalGraphData.nodes.length; i++) {
                if (i !== 0) {
                    const targetId = finalGraphData.nodes[i].id;
                    const sourceId = finalGraphData.nodes[i - 1].id;
                    finalGraphData.edges.push({
                        id: sourceId + "=>" + targetId,
                        source: sourceId,
                        target: targetId,
                        type: finalGraphData.nodes[i].type,
                    });
                }
            }
        }
        return finalGraphData;

    });



interface IUpsertWorkflow {
    payload: ICreateWorkflow,
    onCallback: (isSuccess: boolean, code?: string) => void
}
export const upsertWorkflow = createAsyncThunkPostWrapper<IGraphNode, IUpsertWorkflow>(
    "automation/upsertWorkflow", async ({ payload, onCallback }, { fulfillWithValue }) => {
        const response = await axios_api.post(workflow.upsert_workflow_basic, payload);
        const { nodes, ...rest } = response.data.data;
        const finalData: IGraphNode = {
            id: nodes.id,
            data: {
                label: nodes.data.label,
                payload: rest as ICreateWorkflow,
            },
            position: nodes.position,
            type: nodes.type,
        };
        onCallback(true);
        return finalData
    });


interface IUpsertContitionNode {
    payload: IWorkFlowCondition,
    onCallback: (isSuccess: boolean) => void
}
export const upsertContitionNode = createAsyncThunkPostWrapper<{ node: IGraphNode, isDelete: boolean }, IUpsertContitionNode>(
    "automation/upsertContitionNode", async ({ payload, onCallback }, { fulfillWithValue, getState }) => {
        const isDelete = payload.status === "INACTIVE";
        const workflowNode = getState().automation.graphData.nodes[0].data.payload as ICreateWorkflow;
        const isSelfChanges = workflowNode.real_time_type === "SELF_CHANGES";
        const isCron = workflowNode.run_as == "CRON";
        const conditionVariables: string[] = [];
        let finalDataList: IWorkFlowCondition = {
            ...payload,
            conditions: !isCron ? [...payload.conditions] : [],
        };
        let consitionsList: { [key: string]: IConditionTableRow[] } = {};
        for (let i = 0; i < payload.conditions.length; i++) {
            const condition = payload.conditions[i];
            const firstKey = Object.keys(condition)[0];
            if (firstKey) {
                for (const row of condition[firstKey]) {
                    let column = row.view + "." + row.viewColumn;
                    let columnOrValue = "";
                    if (row.isCompareWithOldValue && isSelfChanges) {
                        columnOrValue =
                            row.view + "." + row.columnCompColumn + ".old_value";
                    } else {
                        if (row.isColumnComparison) {
                            columnOrValue = row.columnCompView + "." + row.columnCompColumn;
                        } else {
                            columnOrValue = row.compareColumnValue as string;
                        }
                    }
                    if (isSelfChanges) {
                        column = column + ".new_value";
                    }
                    conditionVariables.push(column);
                    if (consitionsList[firstKey]) {
                        consitionsList[firstKey].push({
                            ...row,
                            column: column,
                            columnOrValue: columnOrValue,
                        });
                    } else {
                        consitionsList[firstKey] = [
                            {
                                ...row,
                                column: column,
                                columnOrValue: columnOrValue,
                            },
                        ];
                    }
                }
                if (condition[firstKey].length === 0) {
                    consitionsList["and"] = [];
                    finalDataList.conditions[i] = consitionsList;
                    consitionsList = {};
                } else {
                    finalDataList.conditions[i] = consitionsList;
                    consitionsList = {};
                }
            } else {
                consitionsList["and"] = [];
                finalDataList.conditions[i] = consitionsList;
                consitionsList = {};
            }
        }
        if (!isCron) {
            finalDataList.condition_variables = conditionVariables;
        }

        const response = await axios_api.post(workflow.upsert_workflow_condition, {
            ...finalDataList,
            workflow_basic_code: workflowNode.workflow_basic_code,
            conditions: isCron ? {} : finalDataList.conditions,
        });
        const { nodes, ...rest } = response.data.data;
        const finalData: IGraphNode = {
            id: nodes.id,
            data: {
                label: nodes.data.label,
                payload: rest as IWorkFlowCondition,
            },
            position: nodes.position,
            type: nodes.type,
        };
        onCallback(true);
        return { node: finalData, isDelete }
    });




interface IUpsertActionNode {
    payload: IWorkFlowAction,
    onCallback: (isSuccess: boolean) => void
}
export const upsertActionNode = createAsyncThunk<{ node: IGraphNode, isDelete: boolean }, IUpsertActionNode>(
    "automation/upsertActionNode", async ({ payload, onCallback }, { fulfillWithValue, rejectWithValue, dispatch, abort, getState }) => {
        try {
            dispatch(openLoaderWithMessage("Saving your Changes..!"))
            const isDelete = payload.status === "INACTIVE";
            const state = getState() as IStoreState;
            const workflowNode = state.automation.graphData.nodes[0].data.payload;
            const conditionNode = state.automation.graphData.nodes.find((x) => x.type === "conditionNode");

            if (!conditionNode) {
                onCallback(false);
                return rejectWithValue("Workflow node not found.");
            }
            const res = await axios_api.post(workflow.upsert_workflow_action, {
                ...payload,
                workflow_basic_code: workflowNode.workflow_basic_code,
                workflow_condition_code: (
                    conditionNode.data.payload as IWorkFlowCondition
                ).workflow_condition_code,
            });

            const { nodes, ...rest } = res.data.data;

            if (!nodes || typeof nodes !== "object" || !nodes.id) {
                console.error("Invalid nodes structure in API response:", nodes);
                throw new Error("Invalid 'nodes' structure");
            }

            const finalData: IGraphNode = {
                id: nodes.id,
                data: {
                    label: nodes.data.label,
                    payload: rest as IWorkFlowAction,
                },
                position: nodes.position,
                type: nodes.type,
            };
            onCallback(true);
            return fulfillWithValue({ node: finalData, isDelete })
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : error as string
            dispatch(displaySnackBarDialog({
                type: "error",
                title: "Error",
                content: errorMessage
            }))
            return rejectWithValue(errorMessage);

        } finally {
            dispatch(closeLoaderWithMessage())
        }
    });



interface IupsertFinishEmailActionNode {
    payload: IWorkflowFinishEmail,
    onCallback: (isSuccess: boolean) => void
}
export const upsertFinishEmailActionNode = createAsyncThunk<{ node: IGraphNode, isDelete: boolean }, IupsertFinishEmailActionNode>(
    "automation/upsertFinishEmailActionNode", async ({ payload, onCallback }, { getState, dispatch, fulfillWithValue, rejectWithValue }) => {
        try {
            dispatch(openLoaderWithMessage("Saving your Changes..!"))
            const isDelete = payload.status === "INACTIVE";
            const state = getState() as IStoreState;
            const workflowNode = state.automation.graphData.nodes[0].data.payload;
            const actionNode = state.automation.graphData.nodes.find((x) =>
                [
                    "actionNodeEmail",
                    "actionNodeSMS",
                    "actionNodeWhatsApp",
                    "actionNodeModification",
                ].includes(x.type)
            );

            if (!actionNode) {
                onCallback(false);
                return rejectWithValue("Workflow node not found.");
            }
            const finalPayload: IWorkflowFinishEmailAPIPayloadResponse = {
                workflow_action_email_code: payload.workflow_action_email_code,
                workflow_basic_code: workflowNode.workflow_basic_code as string,
                workflow_action_code: (actionNode.data.payload as IWorkFlowAction).workflow_action_code as string,
                call_type: payload.call_type,
                to: [],
                cc: [],
                bcc: [],
                sending_order: payload.sending_order,
                template_code: payload.template_code,
                nodes: payload.nodes,
                status: payload.status,
            };

            if (payload.call_type === "VARIABLE") {
                const varaibleTo: string[] = [];
                const varaibleCC: string[] = [];
                const varaibleBcc: string[] = [];
                for (const variable of payload.variablesTo) {
                    varaibleTo.push(variable.view + "." + variable.columnName);
                }
                for (const variable of payload.variablesCC) {
                    varaibleCC.push(variable.view + "." + variable.columnName);
                }
                for (const variable of payload.variablesBCC) {
                    varaibleBcc.push(variable.view + "." + variable.columnName);
                }
                finalPayload.to = varaibleTo;
                finalPayload.cc = varaibleCC;
                finalPayload.bcc = varaibleBcc;
            } else if (payload.call_type === "API") {
                for (const endPoint of payload.apiTo) {
                    finalPayload.to.push({
                        ...endPoint,
                        api: endPoint.api,
                        emailColumn: endPoint.emailColumn,
                        userName: endPoint.userName,
                    });
                }
                for (const endPoint of payload.apiCC) {
                    finalPayload.cc.push({
                        ...endPoint,
                        api: endPoint.api,
                        emailColumn: endPoint.emailColumn,
                        userName: endPoint.userName,
                    });
                }
                for (const endPoint of payload.apiBCC) {
                    finalPayload.bcc.push({
                        ...endPoint,
                        api: endPoint.api,
                        emailColumn: endPoint.emailColumn,
                        userName: endPoint.userName,
                    });
                }
            }
            else if (payload.call_type === "EMAIL_ADDRESS") {
                finalPayload.to = payload.emailsTo;
                finalPayload.cc = payload.emailsCC;
                finalPayload.bcc = payload.emailsBCC;
            }

            const res = await axios_api.post(workflow.upsert_workflow_action_email, {
                ...finalPayload,
            });

            const { nodes, ...rest } = parseFinialEmailApiToLocalPayload(
                res.data.data
            );

            const finalData: IGraphNode = {
                id: nodes.id,
                data: {
                    label: nodes.data.label,
                    payload: rest as IWorkflowFinishEmail,
                },
                position: nodes.position,
                type: nodes.type,
            };
            onCallback(true);
            return fulfillWithValue({ node: finalData, isDelete })

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : error as string
            dispatch(displaySnackBarDialog({
                type: "error",
                title: "Error",
                content: errorMessage
            }))
            return rejectWithValue(errorMessage);

        } finally {
            dispatch(closeLoaderWithMessage())
        }

    });